import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import _ from 'underscore';
import {
  Button,
  MenuButton,
  MenuList,
  MenuDivider,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  shorthands,
  tokens,
  Label,
  Input,
  useId,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  menuPopover: {
    minWidth: '180px',
    ...shorthands.padding('4px'),
  },
  menuButton: {
    // This is a known fluent ui issue between Popover and MenuButton: https://github.com/microsoft/fluentui/issues/31804
    // This bug has been fixed in the latest version, but as we use legacy version, update the style as a workaround.
    backgroundColor: 'transparent',
    '&[aria-expanded="true"]': {
      backgroundColor: '#EBEBEB',
    },
  },
  unselectedMenuButton: {
    '&[aria-expanded="false"]': {
      fontWeight: 'unset',
    },
  },
  enabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForeground2BrandHover,
  },
  disabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForegroundDisabled,
  },
});

const useCustomItemStyles = makeStyles({
  minDuration: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '28px',
    marginRight: '28px',
    maxWidth: '124px',
  },
  customDuration: {
    '& .invalidInput': {
      '&:focus-within': {
        '::after': {
          ...shorthands.borderBottom('2px', 'solid', 'rgb(164, 38, 44)'),
        },
      },
      ...shorthands.borderBottom('1px', 'solid', 'rgb(164, 38, 44)'),
    },
  },
  maxDuration: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '28px',
    marginRight: '28px',
    marginBottom: '6px',
    maxWidth: '124px',
  },
  input: {
    paddingRight: '0px',
    height: '32px',
    lineHeight: '32px',
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  contentAfter: {
    backgroundColor: tokens.colorNeutralBackground3,
    paddingRight: '6px',
    paddingLeft: '8px',
  },
  contentLabel: {
    marginBottom: '6px',
    marginTop: '8px',
  },
});

export const VideoDurationFilter = (props) => {
  const {
    i18n,
    defaultMaxDuration,
    defaultMinDuration,
    onChange,
    forceClear,
  } = props;

  const minDurationId = useId('minDuration');
  const afterMinDurationId = useId('afterMinDurationId');
  const maxDurationId = useId('maxDuration');
  const afterMaxDurationId = useId('afterMaxDurationId');
  const customClasses = useCustomItemStyles();
  const classes = useStyles();

  // use ref to handle focus switching when pressing tab key
  const minDurationRef = useRef(null);
  const maxDurationRef = useRef(null);

  const [minDuration, setMinDuration] = useState(defaultMinDuration);
  const [maxDuration, setMaxDuration] = useState(defaultMaxDuration);

  const [minDurationInvalid, setMinDurationInvalid] = useState(false);
  const [maxDurationInvalid, setMaxDurationInvalid] = useState(false);

  const onChangeCallback = useCallback((setter, newValue) => {
    const val = parseInt(newValue, 10);
    if (_.isEmpty(newValue) || _.isNaN(val)) {
      setter(null);
      return;
    }
    if (!_.isNaN(val) && val >= 0 && val <= 1000000) {
      setter(val);
    }
  }, []);

  const onMinDurationChange = useCallback((event, newValue) => {
    onChangeCallback(setMinDuration, newValue);
    if (_.isEmpty(newValue) || maxDuration > newValue) {
      setMinDurationInvalid(false);
      setMaxDurationInvalid(false);
    } else if ((maxDuration && newValue >= maxDuration)) {
      setMinDurationInvalid(true);
    } else {
      setMinDurationInvalid(false);
    }
  }, [maxDuration, onChangeCallback]);

  const onMaxDurationChange = useCallback((event, newValue) => {
    onChangeCallback(setMaxDuration, newValue);
    if (_.isEmpty(newValue) || newValue > minDuration) {
      setMinDurationInvalid(false);
      setMaxDurationInvalid(false);
    } else if (newValue <= minDuration) {
      setMaxDurationInvalid(true);
    } else {
      setMaxDurationInvalid(false);
    }
  }, [onChangeCallback, minDuration]);

  const onClear = useCallback(() => {
    setMinDuration(null);
    setMaxDuration(null);
    if (!(defaultMinDuration || defaultMaxDuration)) {
      // trigger onchange only when dropdown list is closed
      onChange({ newMinDuration: null, newMaxDuration: null });
    }
    if (minDurationRef.current) {
      minDurationRef.current.focus();
    }
  }, [defaultMinDuration, defaultMaxDuration, onChange]);

  const title = useMemo(() => {
    if (minDuration && maxDuration && !minDurationInvalid && !maxDurationInvalid) {
      return `${minDuration}s < ${i18n.getString(_TL_('Duration'))} < ${maxDuration}s`;
    }
    if (minDuration && !minDurationInvalid) {
      return `${i18n.getString(_TL_('Duration'))} > ${minDuration}s`;
    }
    if (maxDuration && !maxDurationInvalid) {
      return `${i18n.getString(_TL_('Duration'))} < ${maxDuration}s`;
    }
    return '';
  }, [minDuration, maxDuration, minDurationInvalid, maxDurationInvalid, i18n]);

  const focusTextField = useCallback(textFieldRef => (event) => {
    if (event.key === 'Tab') {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
      if (event.stopPropagation) { event.stopPropagation(); }
      if (event.preventDefault) { event.preventDefault(); }
    }
  }, []);

  const onMinDurationKeyUp = useMemo(() => focusTextField(maxDurationRef), [focusTextField]);
  const onMaxDurationKeyUp = useMemo(() => focusTextField(minDurationRef), [focusTextField]);

  const onDropdownDismiss = useCallback(() => {
    if (defaultMinDuration !== minDuration || defaultMaxDuration !== maxDuration) {
      onChange({ newMinDuration: minDurationInvalid ? null : minDuration, newMaxDuration: maxDurationInvalid ? null : maxDuration });
    }
  }, [defaultMinDuration, minDuration, defaultMaxDuration, maxDuration, onChange, minDurationInvalid, maxDurationInvalid]);


  const [isOpen, setIsOpen] = useState(false);
  const [menuList, setMenuList] = useState(null);

  const isSelected = useMemo(() => minDuration || maxDuration, [minDuration, maxDuration]);

  const onOpenChange = (e, data) => {
    setIsOpen(data.open);
  };

  const clear = () => {
    setIsOpen(false);
    onClear();
  };

  useEffect(() => {
    if (forceClear) {
      clear();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClear]);

  useEffect(() => {
    if (!isOpen) {
      onDropdownDismiss();
    }
  }, [isOpen, onDropdownDismiss]);

  useEffect(() => {
    if (menuList) {
      // as we are using custom input, we need to remove menu role for accessibility
      menuList.removeAttribute('role');
    }
  }, [menuList]);

  return (
    <Popover trapFocus open={isOpen} onOpenChange={onOpenChange} positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <MenuButton appearance="subtle" className={isSelected ? mergeClasses(classes.menuButton, 'video-duration-button') : mergeClasses(classes.menuButton, classes.unselectedMenuButton, 'video-duration-button')}>
          {isSelected ? title : i18n.getString(_TL_('Video duration'))}
        </MenuButton>
      </PopoverTrigger>

      <PopoverSurface className={mergeClasses(classes.menuPopover, 'video-duration-menu')}>
        <MenuList ref={setMenuList}>
          <div className={mergeClasses('custom-duration', customClasses.customDuration)}>
            <div className={mergeClasses(customClasses.minDuration, 'min-duration')}>
              <Label className={customClasses.contentLabel} htmlFor={minDurationId}>{i18n.getString(_TL_('Min. duration'))}</Label>
              <Input
                ref={minDurationRef}
                className={minDurationInvalid ? mergeClasses(customClasses.input, 'invalidInput') : customClasses.input}
                value={minDuration ? minDuration.toString() : ''}
                onChange={(ev, data) => onMinDurationChange(ev, data.value)}
                contentAfter={
                  <div className={customClasses.contentAfter} id={afterMinDurationId}>
                    {i18n.getString(_TL_('s'))}
                  </div>
                }
                aria-labelledby={`${minDurationId} ${afterMinDurationId}`}
                id={minDurationId}
                onKeyDown={onMinDurationKeyUp}
              />
            </div>
            <div className={mergeClasses(customClasses.maxDuration, 'max-duration')}>
              <Label className={customClasses.contentLabel} htmlFor={maxDurationId}>{i18n.getString(_TL_('Max. duration'))}</Label>
              <Input
                ref={maxDurationRef}
                className={maxDurationInvalid ? mergeClasses(customClasses.input, 'invalidInput') : customClasses.input}
                value={maxDuration ? maxDuration.toString() : ''}
                onChange={(ev, data) => onMaxDurationChange(ev, data.value)}
                contentAfter={
                  <div className={customClasses.contentAfter} id={afterMaxDurationId}>
                    {i18n.getString(_TL_('s'))}
                  </div>
                }
                aria-labelledby={`${maxDurationId} ${afterMaxDurationId}`}
                id={maxDurationId}
                onKeyDown={onMaxDurationKeyUp}
              />
            </div>
          </div>
          <MenuDivider />
        </MenuList>
        <div>
          <Button
            className={isSelected ? mergeClasses('clear-button', classes.enabledClearButton) : classes.disabledClearButton}
            disabled={!isSelected}
            appearance="transparent"
            icon={<Dismiss20Regular />}
            onClick={() => clear()}
          >
            {i18n.getString(_TL_('Clear'))}
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
};

